import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { useColors } from '@/contexts/ThemeContext';

// ============================================================================
// TYPES
// ============================================================================

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'size'> {
  /** Label text displayed above select */
  label?: string;
  /** Error message - displays in red below select */
  error?: string;
  /** Helper text - displays in gray below select */
  helperText?: string;
  /** Full width select */
  fullWidth?: boolean;
  /** Show required asterisk */
  required?: boolean;
  /** Label size - 'sm' or 'xs' */
  labelSize?: 'sm' | 'xs';
  /** Placeholder text for empty option */
  placeholder?: string;
  /** Options array */
  options: SelectOption[];
  /** Size — base (h-10 default), sm (h-9), xs (h-8) */
  size?: 'base' | 'sm' | 'xs';
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Select Component
 * 
 * Professional select field with:
 * - Label support
 * - Error states
 * - Helper text
 * - Required indicator
 * - Professional color system
 * - Consistent styling
 * 
 * @example
 * // Basic select
 * <Select 
 *   label="País" 
 *   options={[{ value: 'CO', label: 'Colombia' }]} 
 * />
 * 
 * // With error
 * <Select 
 *   label="País" 
 *   error="Selecciona un país" 
 *   options={countries} 
 * />
 * 
 * // Required field
 * <Select label="Moneda" required options={currencies} />
 */
const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({
    className,
    label,
    error,
    helperText,
    id,
    fullWidth = true,
    disabled,
    required,
    labelSize = 'sm',
    placeholder,
    options,
    value,
    size = 'base',
    ...props
  }, ref) => {
    const sizeClass =
      size === 'xs' ? 'h-8 text-sm' : size === 'sm' ? 'h-9 text-sm' : 'h-10 text-sm';
    const colors = useColors();
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const hasError = !!error;

    return (
      <div className={cn('space-y-1.5', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'block font-medium',
              labelSize === 'xs' ? 'text-xs text-muted-foreground' : 'text-sm'
            )}
            style={{
              color: labelSize === 'xs' ? undefined : colors.foreground.primary,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {label}
            {required && (
              <span style={{ color: colors.status.error, marginLeft: '4px' }}>*</span>
            )}
          </label>
        )}

        {/* Select */}
        <select
          ref={ref}
          id={selectId}
          disabled={disabled}
          value={value}
          className={cn(
            'flex w-full rounded-lg px-3 py-2',
            sizeClass,
            'transition-all duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'appearance-none bg-no-repeat cursor-pointer',
            className,
          )}
          style={{
            backgroundColor: colors.background.primary,
            color: colors.foreground.primary,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: hasError 
              ? colors.status.error 
              : colors.border.primary,
            fontFamily: 'var(--font-system, "Area", sans-serif)',
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 12 8'%3e%3cpath stroke='%23737373' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M1 1.5l5 5 5-5'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.75rem center',
            backgroundSize: '0.75em 0.5em',
            paddingRight: '2.25rem',
          }}
          {...props}
        >
          {placeholder && (
            <option value="">{placeholder}</option>
          )}
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Error message */}
        {error && (
          <p 
            className="text-xs font-medium"
            style={{
              color: colors.status.error,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {error}
          </p>
        )}

        {/* Helper text */}
        {helperText && !error && (
          <p 
            className="text-xs"
            style={{
              color: colors.foreground.secondary,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
export default Select;
